import type { Config } from 'dompurify';
import type { OwaSanitizerConfig } from '../owaSanitizerConfig';
import type { ISanitizerConfigAdapter } from './ISanitizerConfigAdapter';
import { OWA_ADDITIONAL_ATTRIBUTES, OWA_ADDITIONAL_TAGS } from '../sanitizerDefaults';

/**
 * Concrete adapter that translates OwaSanitizerConfig to both
 * DOMPurify Config and native SanitizerConfig formats.
 *
 * Follows the Adapter Design Pattern — a single class implements
 * ISanitizerConfigAdapter with two target-specific methods.
 */
export class SanitizerConfigAdapter implements ISanitizerConfigAdapter {
    /**
     * Converts an OwaSanitizerConfig to a DOMPurify Config.
     *
     * Define the base configuration with the html profile, then apply additions/removals based on the provided config.:
     * - USE_PROFILES: { html: true } as baseline
     * - ADD_TAGS/ADD_ATTR for additions
     * - FORBID_TAGS/FORBID_ATTR for removals
     */
    toDomPurifyConfig(config: OwaSanitizerConfig): Config {
        const domPurifyConfig: Config = {
            USE_PROFILES: { html: true },
        };

        if (config.svgProfile) {
            domPurifyConfig.USE_PROFILES = { ...domPurifyConfig.USE_PROFILES, svg: true };
        }

        // Merge baseline + additional attributes (deduped)
        const additionalAttrs = config.additionalAttributes ?? [];
        domPurifyConfig.ADD_ATTR = Array.from(
            new Set([...OWA_ADDITIONAL_ATTRIBUTES, ...additionalAttrs])
        );

        if (config.additionalTags?.length) {
            domPurifyConfig.ADD_TAGS = config.additionalTags;
        }

        if (config.forbiddenTags?.length) {
            domPurifyConfig.FORBID_TAGS = config.forbiddenTags;
        }

        if (config.forbiddenAttributes?.length) {
            domPurifyConfig.FORBID_ATTR = config.forbiddenAttributes;
        }

        if (config.allowUnknownProtocols) {
            domPurifyConfig.ALLOW_UNKNOWN_PROTOCOLS = true;
        }

        if (config.allowDataAttributes === false) {
            domPurifyConfig.ALLOW_DATA_ATTR = false;
        }

        if (config.wholeDocument) {
            domPurifyConfig.WHOLE_DOCUMENT = true;
        }

        if (config.forceBody) {
            domPurifyConfig.FORCE_BODY = true;
        }

        if (config.additionalUriSafeAttributes?.length) {
            domPurifyConfig.ADD_URI_SAFE_ATTR = config.additionalUriSafeAttributes;
        }

        return domPurifyConfig;
    }

    /**
     * Converts an OwaSanitizerConfig to a native Sanitizer instance.
     *
     * Starts from the default Sanitizer (XSS-safe baseline of all standard
     * HTML elements and attributes), then applies incremental additions
     * and removals. The Sanitizer object auto-normalizes conflicts
     * (e.g. an element in both allow and remove lists).
     */
    toNativeSanitizerConfig(config: OwaSanitizerConfig): Sanitizer {
        const sanitizer = new Sanitizer();

        // OWA-specific elements always added
        for (const tag of OWA_ADDITIONAL_TAGS) {
            sanitizer.allowElement(tag);
        }

        // OWA-specific attributes always added
        for (const attr of OWA_ADDITIONAL_ATTRIBUTES) {
            sanitizer.allowAttribute(attr);
        }

        // Caller additions
        for (const tag of config.additionalTags ?? []) {
            sanitizer.allowElement(tag.toLowerCase());
        }
        for (const attr of config.additionalAttributes ?? []) {
            sanitizer.allowAttribute(attr.toLowerCase());
        }

        // Caller removals (auto-normalized by Sanitizer)
        for (const tag of config.forbiddenTags ?? []) {
            sanitizer.removeElement(tag.toLowerCase());
        }
        for (const attr of config.forbiddenAttributes ?? []) {
            sanitizer.removeAttribute(attr.toLowerCase());
        }

        // Options
        // Default data-* to true — matches DOMPurify's ALLOW_DATA_ATTR default and is required
        // for OWA email bodies which use data-imagetype, data-custom, data-outlook-trace, etc.
        sanitizer.setComments(config.allowComments ?? false);
        sanitizer.setDataAttributes(config.allowDataAttributes ?? true);

        // Defense-in-depth: strip any XSS-unsafe elements/attributes
        // that may have been added via caller config (e.g. script, iframe, onclick).
        sanitizer.removeUnsafe();

        return sanitizer;
    }
}

/** Lazily-initialized singleton — avoids module-scope constructor call. */
let _instance: SanitizerConfigAdapter | undefined;

export function getSanitizerConfigAdapter(): SanitizerConfigAdapter {
    if (!_instance) {
        _instance = new SanitizerConfigAdapter();
    }
    return _instance;
}
